// src/pages/product/ProductBulkUpload.tsx
//
// ProductBulkUpload
// - Admin page to bulk create/update products via Excel upload.
// - Header: inject "Download Template" action only (via AppPageHeader slots).
// - Upload flow: upload -> refresh history -> navigate to detail page for review.
// - History list: pagination + go-to-page, navigation ONLY via Index cell, delete via icon.
// - UX parity with eSIM bulk pages:
//   - Status shown as user-friendly Chip
//   - isDeleted=true => row inactive + Status overridden to "Deleted" + delete disabled/hidden
//   - Failed count emphasized
//   - Clamp page when totalPages changes
//
// NOTE: DragDropFile / confirm / systemAlert are shared components (keep).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeleteOutline, FileDownload } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { useUploadHistories } from '@queries/productBulk/useUploadHistories';
import { useUploadHistoriesCount } from '@queries/productBulk/useUploadHistoriesCount';
import {
  deleteUploadHistory,
  uploadFile,
  type GetUploadHistoriesResponse,
} from '@apis/productBulk/upload';

import { timestampToDate } from '@utils/time';
import { downloadFile } from '@utils/file';
import { PRODUCT_BULK_UPLOAD_TEMPLATE } from '@constants/templates';

import DragDropFile from '@components/common/DragDropFile';

import { usePageHeader } from '@components/common/pageHeader.context';
import type { HeaderAction } from '@components/common/pageHeader.types';

import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import {
  StandardTableToolbar,
  StandardTableFrame,
  StandardPagination,
  cellSx,
} from '@components/common/table';

import { Status } from '@models/product';

const PAGE_SIZE = 50;

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const formatNumber = (n: number | undefined | null) => Number(n ?? 0).toLocaleString();

const dash = (v: unknown) => {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'string') return v.trim() ? v : '-';
  return String(v);
};

const UploadTypeChip = ({
  type,
}: {
  type: GetUploadHistoriesResponse['uploadType'] | string;
}) => {
  const t = String(type ?? '').trim().toUpperCase();
  if (t === 'CREATE') return <Chip size="small" label="Create" color="success" variant="outlined" />;
  if (t === 'UPDATE') return <Chip size="small" label="Update" color="info" variant="outlined" />;
  return <Chip size="small" label={dash(type)} variant="outlined" />;
};

const StatusChip = ({ status, isDeleted }: { status: Status; isDeleted: boolean }) => {
  if (isDeleted) return <Chip size="small" label="Deleted" variant="filled" />;

  switch (status) {
    case Status.COMPLETED:
      return <Chip size="small" label="Completed" color="success" variant="filled" />;
    case Status.PROCESSING:
      return <Chip size="small" label="Processing" color="primary" variant="outlined" />;
    case Status.PENDING:
      return <Chip size="small" label="Pending" color="warning" variant="filled" />;
    default:
      return <Chip size="small" label={dash(status)} variant="outlined" />;
  }
};

/**
 * ProductBulkUpload
 *
 * Header standard:
 * - Title/breadcrumb/back: route handle.pageHeader
 * - This page injects ONLY actions: "Download Template"
 */
const ProductBulkUpload = () => {
  const navigate = useNavigate();

  const confirm = useConfirm();
  const systemAlert = useSystemAlert();
  const { setPageHeaderSlots } = usePageHeader();

  const [page, setPage] = useState(1);

  const { data: historiesData, refetch, isLoading: isListLoading = false } = useUploadHistories({ page });

  const {
    data: totalCount,
    refetch: refetchCount,
    isLoading: isCountLoading = false,
  } = useUploadHistoriesCount();

  const rows = (historiesData?.data ?? []) as GetUploadHistoriesResponse[];

  const totalPages = useMemo(() => {
    const count = totalCount ?? 0;
    return Math.max(1, Math.ceil(count / PAGE_SIZE));
  }, [totalCount]);

  // Safety: clamp page when totalPages changes (e.g., after delete)
  useEffect(() => {
    setPage((prev) => clamp(prev, 1, totalPages));
  }, [totalPages]);

  const handleChangePage = useCallback(
    (next: number) => setPage(clamp(next, 1, totalPages)),
    [totalPages],
  );

  const refresh = useCallback(async () => {
    try {
      await Promise.all([refetch(), refetchCount()]);
    } catch {
      // best-effort
    }
  }, [refetch, refetchCount]);

  const handleNavigateDetail = useCallback(
    (uploadId: number) => {
      if (!Number.isFinite(uploadId) || uploadId <= 0) return;
      navigate(`/products/upload/${uploadId}`);
    },
    [navigate],
  );

  const handleConfirmFile = useCallback(
    async (file: File) => {
      try {
        const { data: createdIds, message } = await uploadFile(file);

        if (message !== 'OK') {
          systemAlert.error(message || 'Upload failed.');
          return;
        }

        await refresh();

        const uploadId = Number(createdIds?.[0]);
        if (!Number.isFinite(uploadId) || uploadId <= 0) {
          systemAlert.error('Upload succeeded but returned an invalid uploadId.');
          return;
        }

        systemAlert.success('Upload started.');
        navigate(`/products/upload/${uploadId}`);
      } catch {
        systemAlert.error('Upload failed.');
      }
    },
    [navigate, refresh, systemAlert],
  );

  const handleDelete = useCallback(
    async (uploadId: number) => {
      const ok = await confirm({
        priority: 'danger',
        title: 'Delete upload?',
        description: 'This will delete this upload and all related product records. Continue?',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        autoFocusConfirm: false,
      });
      if (!ok) return;

      try {
        await deleteUploadHistory(uploadId);
        systemAlert.success('Deleted.');
      } catch {
        systemAlert.error('Failed to delete.');
      } finally {
        await refresh();
      }
    },
    [confirm, refresh, systemAlert],
  );

  const handleDownloadTemplate = useCallback(() => {
    downloadFile(PRODUCT_BULK_UPLOAD_TEMPLATE, 'product_bulk_upload_template');
    systemAlert.success('Template download started.');
  }, [systemAlert]);

  useEffect(() => {
    const actions: HeaderAction[] = [
      {
        key: 'download-template',
        label: 'Download Template',
        onClick: handleDownloadTemplate,
        startIcon: <FileDownload />,
        variant: 'outlined',
        color: 'primary',
        primary: true,
      },
    ];

    setPageHeaderSlots({ actions });

    // Clear on unmount to avoid action leakage across routes.
    return () => setPageHeaderSlots({});
  }, [handleDownloadTemplate, setPageHeaderSlots]);

  const isLoading = Boolean(isListLoading || isCountLoading);

  // index, filename, uploadType, total, ok, failed, status, createdAt, delete(icon)
  const columnsCount = 9;

  const head = useMemo(
    () => (
      <TableRow>
        <TableCell width={70} align="right" sx={cellSx.nowrap}>
          #
        </TableCell>
        <TableCell sx={cellSx.nowrap}>File</TableCell>
        <TableCell align="center" sx={cellSx.nowrap}>
          Type
        </TableCell>
        <TableCell align="right" sx={cellSx.nowrap}>
          Total
        </TableCell>
        <TableCell align="right" sx={cellSx.nowrap}>
          OK
        </TableCell>
        <TableCell align="right" sx={cellSx.nowrap}>
          Failed
        </TableCell>
        <TableCell align="center" sx={cellSx.nowrap}>
          Status
        </TableCell>
        <TableCell align="center" sx={cellSx.nowrap}>
          Created
        </TableCell>
        <TableCell align="center" sx={cellSx.nowrap}>
          Delete
        </TableCell>
      </TableRow>
    ),
    [],
  );

  return (
    <Box width="100%">
      {/* Upload area */}
      <Paper
        sx={(theme) => ({
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: `1px dashed ${theme.palette.divider}`,
        })}
      >
        <DragDropFile onConfirmFile={handleConfirmFile} />
      </Paper>

      <Typography variant="h6" fontWeight={800} mb={1}>
        Upload History
      </Typography>

      <StandardTableToolbar
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={totalCount ?? 0}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      <Box mt={1}>
        <StandardTableFrame<GetUploadHistoriesResponse>
          head={head}
          rows={rows}
          getRowKey={(row) => String(row.productBulkUploadId)}
          columnsCount={columnsCount}
          isLoading={isLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No upload history found."
          minWidth={1100}
          getRowStatus={(row) => (row.isDeleted ? 'inactive' : 'normal')}
          renderRow={(row, index) => {
            const uploadId = Number(row.productBulkUploadId);
            const canNavigate = Number.isFinite(uploadId) && uploadId > 0;
            const isDeleted = Boolean(row.isDeleted);
            const failed = Number(row.failedCount ?? 0);

            return (
              <>
                {/* Index-only navigation (click + keyboard) */}
                <TableCell
                  align="right"
                  role={canNavigate ? 'button' : undefined}
                  tabIndex={canNavigate ? 0 : -1}
                  sx={{
                    ...cellSx.number,
                    cursor: canNavigate ? 'pointer' : 'default',
                    textDecoration: canNavigate ? 'underline' : 'none',
                    textUnderlineOffset: canNavigate ? '3px' : undefined,
                    '&:hover': canNavigate ? { opacity: 0.85 } : undefined,
                  }}
                  onClick={() => {
                    if (!canNavigate) return;
                    handleNavigateDetail(uploadId);
                  }}
                  onKeyDown={(e) => {
                    if (!canNavigate) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNavigateDetail(uploadId);
                    }
                  }}
                >
                  {(page - 1) * PAGE_SIZE + index + 1}
                </TableCell>

                <TableCell
                  sx={{
                    ...cellSx.nowrap,
                    maxWidth: 520,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={dash(row.filename)}
                >
                  {dash(row.filename)}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <UploadTypeChip type={row.uploadType} />
                </TableCell>

                <TableCell align="right" sx={cellSx.number}>
                  {formatNumber(row.totalCount)}
                </TableCell>

                <TableCell align="right" sx={{ ...cellSx.number, color: 'success.main', fontWeight: 700 }}>
                  {formatNumber(row.okayCount)}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    ...cellSx.number,
                    fontWeight: failed > 0 ? 900 : 600,
                    color: failed > 0 ? 'error.main' : 'text.secondary',
                  }}
                >
                  {formatNumber(row.failedCount)}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <StatusChip status={row.status} isDeleted={isDeleted} />
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  {row.createdAt ? timestampToDate(row.createdAt) : '-'}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  {!isDeleted && (
                    <Tooltip title="Delete upload" arrow>
                      <IconButton
                        size="small"
                        aria-label="delete upload history"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!Number.isFinite(uploadId) || uploadId <= 0) return;
                          handleDelete(uploadId);
                        }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      <Box mt={2}>
        <StandardPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={totalCount ?? 0}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default ProductBulkUpload;
